// src/features/golf/AcceptInvite.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Button, 
  Alert,
  Spinner
} from 'react-bootstrap';
import { 
  UserPlus, 
  Eye, 
  EyeOff,
  CheckCircle,
  XCircle,
  Shield,
  Phone,
  Mail,
  Sparkles,
  Building2
} from 'lucide-react';
import { businessService } from '../../services';
import './AcceptInvite.css';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [isValidToken, setIsValidToken] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token found. Please check your link.');
      setVerifying(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      const result = await businessService.verifyInviteToken(token);
      if (result.success) {
        setInviteData(result.data);
        setIsValidToken(true);
        setError('');
      } else {
        if (result.status === 404) {
          setError('Invalid or expired invitation link.');
        } else if (result.status === 400) {
          if (result.data?.detail?.includes('expired')) {
            setError('This invitation link has expired. Please request a new one.');
          } else if (result.data?.detail?.includes('activated')) {
            setError('This account is already activated. Please login.');
          } else {
            setError(result.data?.detail || 'Invalid invitation link.');
          }
        } else {
          setError('Failed to verify invitation. Please try again.');
        }
        setIsValidToken(false);
      }
    } catch (err) {
      setError('Failed to verify invitation. Please try again.');
      setIsValidToken(false);
    }
    setVerifying(false);
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 5000);
  };

  const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/\s/g, '');
    // Accept 07XXXXXXXX (10 digits) OR +254XXXXXXXXX (13 digits)
    return /^(07\d{8}|254\d{9})$/.test(cleaned);
  };

  const formatPhoneNumber = (value) => {
    // Remove spaces
    let cleaned = value.replace(/\s/g, '');
    // If user types 254 without +, add it
    if (cleaned.startsWith('254') && !cleaned.startsWith('+')) {
      return '+' + cleaned;
    }
    return cleaned;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    // Remove spaces
    value = value.replace(/\s/g, '');
    // If starts with 254 and no +, add it
    if (value.startsWith('254') && !value.startsWith('+')) {
      value = '+' + value;
    }
    // Only allow +, numbers, and limit to 13 characters
    if (/^\+?[0-9]*$/.test(value) && value.length <= 13) {
      setPhoneNumber(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      showAlert('Password must be at least 8 characters', 'error');
      return;
    }

    if (password !== confirmPassword) {
      showAlert('Passwords do not match', 'error');
      return;
    }

    if (!phoneNumber.trim()) {
      showAlert('Phone number is required', 'error');
      return;
    }

    // Validate phone number format
    if (!validatePhoneNumber(phoneNumber)) {
      showAlert('Phone number must start with 07 (e.g., 0712345678) or +254 (e.g., +254712345678)', 'error');
      return;
    }

    if (!fullName.trim()) {
      showAlert('Full name is required', 'error');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await businessService.acceptInvite(token, fullName, phoneNumber, password);
      
      if (result.success) {
        setSuccess(true);
        showAlert('✅ Account activated successfully! Redirecting to login...', 'success');
        setTimeout(() => {
          navigate('/');
        }, 5000);
      } else {
        if (result.status === 400) {
          if (result.data?.detail?.includes('expired')) {
            showAlert('This invitation link has expired. Please request a new one.', 'error');
          } else if (result.data?.detail?.includes('activated')) {
            showAlert('This account is already activated. Please login.', 'error');
          } else {
            showAlert(result.data?.detail || 'Failed to activate account.', 'error');
          }
        } else {
          showAlert('Failed to activate account. Please try again.', 'error');
        }
      }
    } catch (err) {
      showAlert('Failed to activate account. Please try again.', 'error');
    }
    setLoading(false);
  };

  // Show loading state while verifying
  if (verifying) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <Card className="accept-invite-card">
            <Card.Body className="accept-invite-body" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <Spinner animation="border" style={{ color: '#0ea5e9', width: '48px', height: '48px' }} />
              <h4 style={{ marginTop: '20px', color: '#1a1e2b' }}>Verifying invitation...</h4>
              <p style={{ color: '#64748b', marginTop: '8px' }}>Please wait while we confirm your invitation.</p>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  // Show invalid token state
  if (!isValidToken) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <Card className="accept-invite-card">
            <Card.Body className="accept-invite-body" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <XCircle size={32} color="#ef4444" />
              </div>
              <h4 style={{ color: '#1a1e2b', marginBottom: '8px' }}>Invalid Invitation</h4>
              <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto' }}>{error}</p>
              <Button 
                variant="primary" 
                style={{ marginTop: '20px' }}
                onClick={() => navigate('/')}
              >
                Return to Login
              </Button>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-container">
        <Card className="accept-invite-card">
          <Card.Body className="accept-invite-body">
            {/* Alert Messages */}
            {alert.show && (
              <Alert 
                variant={alert.type === 'success' ? 'success' : 'danger'}
                className={alert.type === 'success' ? 'success-alert' : 'error-alert'}
                style={{
                  borderRadius: '12px',
                  padding: '14px 18px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {alert.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                {alert.message}
              </Alert>
            )}

            {/* Header */}
            <div className="accept-invite-header">
              <div className="accept-invite-icon">
                <UserPlus size={28} color="white" />
              </div>
              <div>
                <h1 className="accept-invite-title">Accept Invitation</h1>
                <p className="accept-invite-subtitle">
                  Complete your account setup to join the team
                </p>
              </div>
            </div>

            {/* Invite Info */}
            <div className="invite-info-box">
              <div className="invite-info-item">
                <Building2 size={16} />
                <span>{inviteData?.golfclub_name || 'Golf Club'}</span>
              </div>
            </div>

            {/* Form */}
            <Form onSubmit={handleSubmit}>
              {/* Full Name and Phone Number - Side by Side */}
              <div className="form-row">
                <div className="form-group">
                  <Form.Label className="form-label">
                    Full Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="form-input"
                    required
                    disabled={loading || success}
                  />
                </div>

                <div className="form-group">
                  <Form.Label className="form-label">
                    Phone Number *
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="0712345678 or +254712345678"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className="form-input"
                    required
                    disabled={loading || success}
                  />
                  <small style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px', display: 'block' }}>
                    Must start with 07 or +254
                  </small>
                </div>
              </div>

              {/* Password */}
              <div className="form-group">
                <Form.Label className="form-label">
                  Password *
                </Form.Label>
                <div className="password-wrapper">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password (min 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input password-input"
                    required
                    disabled={loading || success}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <Form.Label className="form-label">
                  Confirm Password *
                </Form.Label>
                <div className="password-wrapper">
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input password-input"
                    required
                    disabled={loading || success}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || success}
                className="submit-button"
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : success ? (
                  <>
                    <CheckCircle size={18} />
                    Account Activated!
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Accept Invitation
                  </>
                )}
              </Button>
            </Form>

            {/* Footer */}
            <div className="accept-invite-footer">
              <span>
                <Shield size={14} />
                Secure connection
              </span>
              <span>
                <Sparkles size={14} />
                Welcome aboard
              </span>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}